/**
 * Venda na Rota (celular) — PDV em etapas: Cliente → Produtos → Pagamento → Revisão e
 * finalização → Resultado e impressão.
 *
 * - Só "Finalizar venda" grava: registra a venda (estoque da carga, financeiro) e, se escolhida
 *   a NF-e, pede a nota na mesma operação. Com o Pedido de Venda, a venda fica "aguardando
 *   faturamento" e a NF-e pode ser emitida depois pelo MESMO pedido.
 * - Sem conexão: a venda fica guardada como INTENÇÃO e é enviada quando a conexão voltar, com a
 *   mesma chave (nunca duplica venda nem nota). Nenhuma tentativa de emitir offline.
 * - O rascunho fica nesta guia: recarregar a página não perde o que foi digitado.
 * - O app que hospeda pode exigir conexão para finalizar (`offlineSales: false`) e guardar o
 *   rascunho e as listas só na memória (`deviceData: "memoria"`): é o que o CaféGestor
 *   Vendedor faz na V1.
 */
import {
  formatMoney,
  type Load,
  type Page,
  type Party,
  type PaymentMethod,
  type ProductListRow,
  type RouteSaleInput,
} from "@cafegestor/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { deviceId, useActiveCompanyId } from "@cafegestor/client";
import {
  addIntent,
  flushIntents,
  loadIntents,
  removeIntent,
  sendIntent,
  setIntentOwner,
  startAutoFlush,
  subscribeIntents,
  type RouteIntent,
} from "@cafegestor/client/offline";
import { Alert, Badge, Button, Icon, Select } from "@cafegestor/ui";
import { PaymentStep, ReviewStep } from "./CheckoutSteps";
import { CustomerStep } from "./CustomerStep";
import { useRotaHost } from "./host";
import { ProductsStep } from "./ProductsStep";
import {
  EMPTY_DRAFT,
  memoryDraftStore,
  pendingItemsOf,
  routeDraft,
  useCached,
  useLoadDetail,
  useOnline,
  useRoutePreview,
  type RouteDraft,
} from "./rota-data";
import { LoadSales, RotaResult } from "./RotaResult";
import {
  availableBase,
  balancesOf,
  cartTotal,
  quantityProblem,
  routeSaleBody,
  type DocumentChoice,
} from "./rota-lib";

const STEPS = ["Cliente", "Produtos", "Pagamento", "Revisão"] as const;

function paymentOf(
  method: PaymentMethod | undefined,
  p: { e2e: string; auth: string },
): RouteSaleInput["payment"] {
  if (!method || !(method.kind === "pix" || method.kind.startsWith("cartao"))) return null;
  if (!p.e2e && !p.auth) return null;
  return {
    pixEndToEnd: p.e2e || null,
    authorizationCode: p.auth || null,
    integrationType: null,
    institutionId: null,
    cardBrand: null,
    nsu: null,
    pixTxid: null,
    terminalId: null,
    paidAt: new Date().toISOString(),
    notes: null,
  };
}

export function RotaPage() {
  const online = useOnline();
  const { userId, offlineSales = true, deviceData = "aparelho" } = useRotaHost();
  const companyId = useActiveCompanyId();
  // rascunho no navegador (ERP Web) ou só na memória do app (CaféGestor Vendedor V1)
  const draftStore = useMemo(
    () =>
      deviceData === "memoria"
        ? memoryDraftStore(`${userId ?? "-"}|${companyId ?? "-"}`)
        : routeDraft,
    [deviceData, userId, companyId],
  );
  const queryClient = useQueryClient();
  // as vendas guardadas pertencem a este usuário nesta empresa
  useEffect(() => setIntentOwner(userId, companyId), [userId, companyId]);
  useEffect(() => startAutoFlush(), []);

  const loads = useCached<Load[]>(companyId, "cargas", "/cargas/abertas");
  const customers = useCached<Page<Party>>(
    companyId,
    "clientes",
    "/cadastros/pessoas?papel=cliente&status=active&limit=200",
  );
  const products = useCached<Page<ProductListRow>>(
    companyId,
    "produtos",
    "/cadastros/produtos?status=active&limit=200",
  );
  const methods = useCached<Page<PaymentMethod>>(
    companyId,
    "formas",
    "/cadastros/formas-pagamento?status=active&limit=100",
  );
  const intents = useSyncExternalStore(subscribeIntents, loadIntents, loadIntents);

  const [draft, setDraftState] = useState<RouteDraft>(() => draftStore.get() ?? EMPTY_DRAFT);
  const setDraft = (patch: Partial<RouteDraft>) =>
    setDraftState((d) => {
      const next = { ...d, ...patch };
      draftStore.set(next);
      return next;
    });
  const [view, setView] = useState<"venda" | "vendas">("venda");
  const [editingItem, setEditingItem] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);
  // a venda em envio não aparece na lista de "guardadas" (recusada, ela volta para a revisão)
  const [sendingKey, setSendingKey] = useState<string | null>(null);
  const [result, setResult] = useState<{ intent: RouteIntent; document: DocumentChoice } | null>(
    null,
  );
  const topRef = useRef<HTMLDivElement | null>(null);

  // uma carga aberta só: já vem escolhida
  const loadList = loads.data ?? [];
  useEffect(() => {
    if (!draft.loadId && loadList.length === 1) setDraft({ loadId: loadList[0]!.id });
    // setDraft é estável na prática (só usa o setter); a dependência real é a lista
  });

  const loadDetail = useLoadDetail(companyId, draft.loadId);
  const balances = useMemo(
    () => (loadDetail.data ? balancesOf(loadDetail.data) : []),
    [loadDetail.data],
  );
  const productRows = useMemo(() => products.data?.items ?? [], [products.data]);
  const pending = useMemo(
    () => pendingItemsOf(intents, draft.loadId, productRows),
    [intents, draft.loadId, productRows],
  );
  const methodList = methods.data?.items ?? [];
  const method = methodList.find((m) => m.id === draft.paymentMethodId);
  const total = cartTotal(draft.lines);

  const body =
    draft.customer && draft.paymentMethodId && draft.lines.length > 0 && draft.loadId
      ? routeSaleBody({
          loadId: draft.loadId,
          customerId: draft.customer.id,
          paymentMethodId: draft.paymentMethodId,
          lines: draft.lines,
          document: draft.document,
          payment: paymentOf(method, draft.payment),
          deviceId: deviceId(),
          previewHash: null,
        })
      : null;
  const preview = useRoutePreview(body, online && draft.step === 3 && draft.document === "nfe");

  const lineProblems = draft.lines
    .map((l) =>
      quantityProblem(l, availableBase(l.productId, balances, draft.lines, pending, l.key)),
    )
    .filter((p): p is string => p !== null);

  function problemOf(step: number): string | null {
    if (!draft.loadId) return "Escolha a carga.";
    if (step >= 0 && !draft.customer) return "Escolha ou cadastre o cliente.";
    if (step >= 1) {
      if (draft.lines.length === 0) return "Inclua pelo menos um produto.";
      if (lineProblems.length > 0) return lineProblems[0]!;
    }
    if (step >= 2 && !method) return "Escolha a forma de pagamento.";
    return null;
  }

  function goTo(step: number) {
    setStepError(null);
    setSendError(null);
    setDraft({ step });
    topRef.current?.scrollIntoView?.({ block: "start" });
  }

  function next() {
    const p = problemOf(draft.step);
    if (p) {
      setStepError(p);
      return;
    }
    goTo(draft.step + 1);
  }

  const nfeBlocked =
    draft.document === "nfe" &&
    online &&
    (preview.isFetching || !preview.data || preview.data.calc.issues.length > 0);

  async function finish() {
    if (sending.current) return; // toque duplo não registra duas vendas
    const p = problemOf(3);
    if (p) {
      setStepError(p);
      return;
    }
    if (!body || !draft.customer) return;
    if (!online && !offlineSales) {
      // V1 do app: a venda só é confirmada com conexão; nada do que foi digitado se perde
      setStepError(
        "Sem conexão: conecte o aparelho à internet para finalizar. O que você digitou continua aqui.",
      );
      return;
    }
    if (nfeBlocked) {
      setStepError(
        preview.data && preview.data.calc.issues.length > 0
          ? "A nota não pode ser emitida agora. Corrija o que falta ou escolha o Pedido de Venda."
          : "Aguarde a conferência da nota.",
      );
      return;
    }
    sending.current = true;
    setBusy(true);
    setStepError(null);
    setSendError(null);
    const document = draft.document;
    const intent = addIntent(`${draft.customer.name} — ${new Date().toLocaleTimeString("pt-BR")}`, {
      ...body,
      previewHash: document === "nfe" ? (preview.data?.hash ?? null) : null,
    });
    setSendingKey(intent.key);
    const done = navigator.onLine ? await sendIntent(intent) : intent;
    sending.current = false;
    setBusy(false);
    setSendingKey(null);
    if (done.state === "erro") {
      // venda recusada (dados, crédito, saldo): a venda continua aqui para corrigir
      removeIntent(intent.key);
      setSendError(done.error ?? "A venda foi recusada.");
      return;
    }
    draftStore.clear();
    setDraftState({ ...EMPTY_DRAFT, loadId: draft.loadId });
    setResult({ intent: done, document });
    void queryClient.invalidateQueries({ queryKey: ["rota", "cache"] });
    void queryClient.invalidateQueries({ queryKey: ["vendas", "pedidos"] });
    topRef.current?.scrollIntoView?.({ block: "start" });
  }

  const waiting = intents.filter((i) => i.state !== "concluida" && i.key !== sendingKey);
  const load = loadList.find((l) => l.id === draft.loadId);
  const showFooter = view === "venda" && !result && !editingItem;

  return (
    <div ref={topRef} className={`mx-auto max-w-xl space-y-4 ${showFooter ? "pb-36" : "pb-6"}`}>
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-[22px] font-bold leading-tight">Venda na Rota</h1>
          <p className="text-[13px] text-muted-foreground">
            {load ? `Carga ${load.number} · ${load.salespersonName}` : "Escolha a carga"}
          </p>
        </div>
        <span
          role="status"
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold ${
            online ? "bg-success-soft text-success-strong" : "bg-warning-soft text-warning-strong"
          }`}
        >
          <Icon name={online ? "wifi" : "wifiOff"} size={16} />
          {online ? "Conectado" : "Sem conexão"}
        </span>
      </header>

      {!online && (
        <p className="rounded-xl bg-warning-soft px-3 py-2.5 text-[13px] text-warning-strong">
          {offlineSales
            ? "Sem conexão: a venda fica guardada neste aparelho e é enviada sozinha quando a conexão voltar. A nota só é pedida depois do envio."
            : "Sem conexão: você pode montar a venda, mas só finaliza com internet. O que você digitou continua aqui."}
        </p>
      )}

      {loadList.length !== 1 && (
        <Select
          aria-label="Carga"
          className="h-12"
          value={draft.loadId}
          onChange={(e) => setDraft({ ...EMPTY_DRAFT, loadId: e.target.value })}
        >
          <option value="">
            {loads.isPending ? "Carregando as cargas…" : "Escolha a carga aberta"}
          </option>
          {loadList.map((l) => (
            <option key={l.id} value={l.id}>
              {l.number} — {l.salespersonName}
            </option>
          ))}
        </Select>
      )}
      {loads.data && loadList.length === 0 && (
        <Alert tone="warn">
          Nenhuma carga aberta para você. A venda na rota sai da carga do veículo: peça ao
          escritório para abrir a carga.
        </Alert>
      )}

      {waiting.length > 0 && <WaitingSales intents={waiting} online={online} />}

      {draft.loadId && (
        <div
          role="tablist"
          aria-label="Venda na rota"
          className="grid grid-cols-2 gap-1 rounded-xl bg-muted p-1"
        >
          {(
            [
              ["venda", "Nova venda"],
              ["vendas", "Vendas desta carga"],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={view === k}
              onClick={() => setView(k)}
              className={`h-10 rounded-lg text-sm font-semibold ${
                view === k ? "bg-card text-foreground shadow-card" : "text-muted-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {draft.loadId && view === "vendas" && <LoadSales loadId={draft.loadId} online={online} />}

      {draft.loadId && view === "venda" && result && (
        <RotaResult
          intent={intents.find((i) => i.key === result.intent.key) ?? result.intent}
          document={result.document}
          onNewSale={() => {
            setResult(null);
            goTo(0);
          }}
          onShowSales={() => {
            setResult(null);
            setView("vendas");
          }}
        />
      )}

      {draft.loadId && view === "venda" && !result && (
        <>
          <Stepper step={draft.step} onStep={(n) => n < draft.step && goTo(n)} />
          <h2 className="text-lg font-semibold">
            {draft.step === 0 && "Quem está comprando?"}
            {draft.step === 1 && "O que vai levar?"}
            {draft.step === 2 && "Como vai pagar?"}
            {draft.step === 3 && "Revise e finalize"}
          </h2>
          {draft.step === 0 && (
            <CustomerStep
              online={online}
              cached={customers.data?.items ?? []}
              customer={draft.customer}
              onSelect={(c) => {
                setStepError(null);
                setDraft({ customer: c });
              }}
            />
          )}
          {draft.step === 1 && draft.customer && (
            <ProductsStep
              companyId={companyId}
              customerId={draft.customer.id}
              online={online}
              balances={balances}
              products={productRows}
              lines={draft.lines}
              pending={pending}
              onLines={(lines) => {
                setStepError(null);
                setDraft({ lines });
              }}
              onEditing={setEditingItem}
            />
          )}
          {draft.step === 2 && (
            <PaymentStep
              methods={methodList}
              methodId={draft.paymentMethodId}
              onMethod={(id) => {
                setStepError(null);
                setDraft({ paymentMethodId: id, payment: { e2e: "", auth: "" } });
              }}
              payment={draft.payment}
              onPayment={(payment) => setDraft({ payment })}
              total={total}
              customer={draft.customer}
            />
          )}
          {draft.step === 3 && draft.customer && (
            <ReviewStep
              customer={draft.customer}
              lines={draft.lines}
              method={method}
              total={total}
              document={draft.document}
              onDocument={(document) => {
                setStepError(null);
                setDraft({ document });
              }}
              online={online}
              preview={preview}
              onEdit={goTo}
            />
          )}
          {sendError && (
            <Alert tone="error">
              <strong>A venda não foi registrada:</strong> {sendError} Corrija e finalize de novo —
              nada foi gravado.
            </Alert>
          )}
        </>
      )}

      {showFooter && draft.loadId && (
        <footer className="fixed inset-x-0 bottom-[var(--rota-footer-bottom,0px)] z-30 border-t border-border bg-card/95 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-pop backdrop-blur">
          <div className="mx-auto max-w-xl space-y-2">
            {stepError && (
              <p role="alert" className="text-sm font-medium text-destructive">
                {stepError}
              </p>
            )}
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-muted-foreground">
                {draft.lines.length} {draft.lines.length === 1 ? "item" : "itens"}
              </span>
              <span className="text-lg font-bold tabular-nums text-primary">
                {total === null ? "Total ao enviar" : `Total ${formatMoney(total)}`}
              </span>
            </div>
            <div className="grid grid-cols-[auto_1fr] gap-2">
              <Button
                variant="secondary"
                size="lg"
                onClick={() => goTo(draft.step - 1)}
                disabled={draft.step === 0 || busy}
              >
                Voltar
              </Button>
              {draft.step < 3 ? (
                <Button size="lg" onClick={next}>
                  Continuar
                  <Icon name="chevronRight" size={20} />
                </Button>
              ) : (
                <Button size="lg" onClick={() => void finish()} disabled={busy}>
                  <Icon name="check" size={20} strokeWidth={2.4} />
                  {busy
                    ? "Registrando..."
                    : online
                      ? draft.document === "nfe"
                        ? "Finalizar e emitir NF-e"
                        : "Finalizar venda"
                      : offlineSales
                        ? "Guardar venda"
                        : "Sem conexão"}
                </Button>
              )}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

function Stepper({ step, onStep }: { step: number; onStep: (n: number) => void }) {
  return (
    <nav aria-label="Etapas da venda">
      <p className="mb-2 text-[13px] font-medium text-muted-foreground">
        Etapa {step + 1} de {STEPS.length} · {STEPS[step]}
      </p>
      <ol className="grid grid-cols-4 gap-1.5">
        {STEPS.map((label, n) => {
          const state = n < step ? "done" : n === step ? "current" : "pending";
          return (
            <li key={label}>
              <button
                type="button"
                onClick={() => onStep(n)}
                disabled={n >= step}
                aria-current={state === "current" ? "step" : undefined}
                aria-label={`${label}${state === "done" ? " (concluída)" : ""}`}
                className="block w-full text-left disabled:cursor-default"
              >
                <span
                  className={`block h-1.5 rounded-full ${
                    state === "pending" ? "bg-border" : "bg-primary"
                  }`}
                />
                <span
                  className={`mt-1 block truncate text-[12px] ${
                    state === "current" ? "font-semibold text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {label}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function WaitingSales({ intents, online }: { intents: RouteIntent[]; online: boolean }) {
  return (
    <section
      aria-label="Vendas guardadas neste aparelho"
      className="space-y-2 rounded-2xl border border-warning/30 bg-warning-soft p-3 text-warning-strong"
    >
      <h2 className="text-sm font-semibold">Vendas guardadas neste aparelho ({intents.length})</h2>
      <ul className="space-y-1.5 text-sm">
        {intents.map((i) => (
          <li key={i.key} className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1 break-words">{i.label}</span>
            <Badge tone={i.state === "erro" ? "error" : "warn"}>
              {i.state === "erro" ? "precisa de revisão" : "aguardando envio"}
            </Badge>
            {i.error && <span className="block w-full text-xs">{i.error}</span>}
            {i.state === "erro" && (
              <Button size="sm" variant="ghost" onClick={() => removeIntent(i.key)}>
                Descartar
              </Button>
            )}
          </li>
        ))}
      </ul>
      {online && (
        <Button size="sm" variant="secondary" onClick={() => void flushIntents()}>
          Enviar agora
        </Button>
      )}
    </section>
  );
}
